import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertTriangle, ChevronDown, ChevronRight, Radio } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getTaskMonitor, dismissProviderError } from '../../services/taskMonitorService';
import type { TaskEntry, TaskStatus, StreamStat, EventBusStats, ProviderHealthEntry } from '../../types/taskMonitor';

const STATUS_BADGE: Record<TaskStatus, { label: string; className: string }> = {
  running:   { label: 'Running',   className: 'bg-blue-500 animate-pulse text-white' },
  completed: { label: 'Completed', className: 'bg-green-600 text-white' },
  failed:    { label: 'Failed',    className: 'bg-red-600 text-white' },
  skipped:   { label: 'Skipped',   className: 'bg-amber-500 text-white' },
  never:     { label: 'Never Run', className: 'bg-slate-500 text-white' },
};

function StatusBadge({ status }: { status: TaskStatus }) {
  const { label, className } = STATUS_BADGE[status];
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

function formatRelative(isoString: string | null): string {
  if (!isoString) return '\u2014';
  const diff = Date.now() - new Date(isoString).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function formatFuture(isoString: string | null): string {
  if (!isoString) return '\u2014';
  const diffMs = new Date(isoString).getTime() - Date.now();
  if (diffMs <= 0) return 'overdue';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'in <1m';
  if (min < 60) return `in ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `in ${hr}h ${min % 60}m`;
  return `in ${Math.floor(hr / 24)}d`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '\u2014';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Strip "marome:events:" prefix for display. */
function shortStreamName(stream: string): string {
  return stream.replace(/^marome:events:/, '');
}

/** Green for 0, amber for any pending > 0. Red alert is shown at card header level via server-side alert flag. */
function PendingBadge({ pending }: { pending: number }) {
  if (pending === 0) {
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-900/40 text-green-400">0</span>;
  }
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-900/40 text-amber-300">{pending}</span>;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  kimi: 'Kimi (Moonshot)',
  exa: 'Exa (web search)',
  brave: 'Brave Search',
};

function ProviderHealthPanel({
  health,
  onDismiss,
}: {
  health: Record<string, ProviderHealthEntry>;
  onDismiss: (provider: string) => void;
}) {
  const entries = Object.entries(health);
  if (entries.length === 0) return null;

  return (
    <div className="bg-slate-800 rounded-lg border border-red-700/60 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-red-900/30 border-b border-red-700/40">
        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
        <span className="text-red-300 font-medium text-sm">Provider Quota / Credit Errors</span>
        <span className="ml-auto text-xs text-red-400 bg-red-900/50 px-2 py-0.5 rounded">
          {entries.length} affected
        </span>
      </div>
      <div className="divide-y divide-slate-700/50">
        {entries.map(([provider, entry]) => (
          <div key={provider} className="px-4 py-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-red-300 text-sm">
                  {PROVIDER_LABELS[provider] ?? provider}
                </span>
                <span className="text-xs text-slate-500">
                  {formatRelative(entry.timestamp)}
                </span>
              </div>
              <p className="text-xs text-slate-400 font-mono break-all leading-relaxed">
                {entry.error}
              </p>
            </div>
            <button
              onClick={() => onDismiss(provider)}
              className="flex-shrink-0 text-xs text-slate-500 hover:text-slate-300 border border-slate-600 hover:border-slate-400 px-2 py-1 rounded transition-colors"
            >
              Dismiss
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventBusCard({ eventBus }: { eventBus: EventBusStats }) {
  const totalPending = eventBus.total_pending;
  const headerBadgeClass =
    totalPending === 0
      ? 'bg-green-900/40 text-green-400'
      : eventBus.alert
      ? 'bg-red-900/50 text-red-300'
      : 'bg-amber-900/40 text-amber-300';

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-700/50">
        <span className="flex items-center gap-2 text-slate-300 font-medium text-sm">
          <Radio className="w-4 h-4" />
          Event Bus — Redis Streams
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${headerBadgeClass}`}>
          {totalPending} pending
        </span>
      </div>

      {eventBus.error ? (
        <div className="px-4 py-3 text-sm text-slate-500">{eventBus.error}</div>
      ) : eventBus.streams.length === 0 ? (
        <div className="px-4 py-3 text-sm text-slate-500">No streams configured.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-700/30">
            <tr className="text-slate-400 text-xs uppercase">
              <th className="text-left px-4 py-2">Stream</th>
              <th className="text-left px-4 py-2">Group</th>
              <th className="text-left px-4 py-2">Pending</th>
              <th className="text-left px-4 py-2">Oldest Pending</th>
              <th className="text-left px-4 py-2">Consumers</th>
            </tr>
          </thead>
          <tbody>
            {eventBus.streams.map((s: StreamStat, i: number) => (
              <tr key={i} className="border-t border-slate-700/50">
                <td className="px-4 py-2 font-mono text-xs text-slate-300">
                  {shortStreamName(s.stream)}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">
                  {s.group}
                </td>
                <td className="px-4 py-2">
                  <PendingBadge pending={s.pending} />
                </td>
                <td className="px-4 py-2 text-xs text-slate-400">
                  {s.oldest_pending_age_s !== null ? `${s.oldest_pending_age_s}s` : '\u2014'}
                </td>
                <td className="px-4 py-2 text-xs text-slate-400">
                  {s.consumers}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!eventBus.error && eventBus.streams.length > 0 && totalPending === 0 && (
        <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-700/50">
          No pending messages — all streams clear.
        </div>
      )}
    </div>
  );
}

export default function TaskMonitor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedTask, setExpandedTask] = useState<string | null>(null);

  // RBAC guard — redirect non-admins
  if (user && !user.is_admin) {
    navigate('/');
    return null;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['task-monitor'],
    queryFn: getTaskMonitor,
    refetchInterval: 30000,
  });

  async function handleDismissProvider(provider: string) {
    try {
      await dismissProviderError(provider);
      queryClient.invalidateQueries({ queryKey: ['task-monitor'] });
    } catch {
      // ignore
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-400">
        <Activity className="w-4 h-4 animate-spin" />
        Loading task monitor...
      </div>
    );
  }
  if (error) {
    return <div className="p-6 text-red-400">Error loading task monitor.</div>;
  }

  const quota = data?.eodhd_quota;
  const quotaPct = quota ? Math.min(100, (quota.used / quota.limit) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-xl font-bold text-slate-100 flex items-center gap-2">
        <Activity className="w-5 h-5" />
        Background Task Monitor
      </h1>

      {/* EODHD Quota Gauge */}
      {quota ? (
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium text-slate-200">EODHD API Quota</span>
            <span className="text-slate-400">
              {quota.used.toLocaleString()} / {quota.limit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${
                quotaPct > 90 ? 'bg-red-500' : quotaPct > 70 ? 'bg-amber-500' : 'bg-green-500'
              }`}
              style={{ width: `${quotaPct}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Resets {new Date(quota.reset_at).toLocaleString()}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700 text-sm text-slate-500">
          EODHD quota unavailable (Redis offline)
        </div>
      )}

      {/* Provider Health */}
      {data?.provider_health && Object.keys(data.provider_health).length > 0 && (
        <ProviderHealthPanel
          health={data.provider_health}
          onDismiss={handleDismissProvider}
        />
      )}

      {/* Task Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr className="text-slate-400 text-xs uppercase">
              <th className="text-left px-4 py-3 w-6" />
              <th className="text-left px-4 py-3">Task</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last Run</th>
              <th className="text-left px-4 py-3">Duration</th>
              <th className="text-left px-4 py-3">Pod</th>
              <th className="text-left px-4 py-3">Next Run</th>
            </tr>
          </thead>
          <tbody>
            {data?.tasks.map((task: TaskEntry) => {
              const status: TaskStatus = (task.last_run?.status as TaskStatus) ?? 'never';
              const isExpanded = expandedTask === task.task_name;
              return (
                <>
                  <tr
                    key={task.task_name}
                    className="border-t border-slate-700/50 cursor-pointer hover:bg-slate-700/30 transition-colors"
                    onClick={() => setExpandedTask(isExpanded ? null : task.task_name)}
                  >
                    <td className="px-4 py-3 text-slate-500">
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3" />
                        : <ChevronRight className="w-3 h-3" />}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-300">
                      {task.task_name}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {formatRelative(task.last_run?.started_at ?? null)}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {formatDuration(task.last_run?.duration_ms ?? null)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {task.last_run?.pod_id ?? '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {formatFuture(task.next_run_at)}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${task.task_name}-history`} className="bg-slate-900/40">
                      <td colSpan={7} className="px-6 py-4">
                        <p className="text-xs font-medium text-slate-400 mb-2">
                          Run History (last 10)
                        </p>
                        {task.history.length === 0 ? (
                          <p className="text-slate-600 text-xs">No history recorded.</p>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="text-left pr-4 pb-1">Time</th>
                                <th className="text-left pr-4 pb-1">Status</th>
                                <th className="text-left pr-4 pb-1">Duration</th>
                                <th className="text-left pr-4 pb-1">Pod</th>
                                <th className="text-left pb-1">Error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {task.history.map((run, i) => (
                                <tr key={i} className="border-t border-slate-700/30">
                                  <td className="pr-4 py-1 text-slate-400">
                                    {formatRelative(run.started_at)}
                                  </td>
                                  <td className="pr-4 py-1">
                                    <StatusBadge status={run.status} />
                                  </td>
                                  <td className="pr-4 py-1 text-slate-400">
                                    {formatDuration(run.duration_ms)}
                                  </td>
                                  <td className="pr-4 py-1 font-mono text-slate-500">
                                    {run.pod_id}
                                  </td>
                                  <td className="py-1 text-red-400">
                                    {run.error_message ?? ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Event Bus Card */}
      {data?.event_bus && <EventBusCard eventBus={data.event_bus} />}
    </div>
  );
}
